import React, {useState} from "react";
import {Context} from "@hotwired/stimulus";
import {ReactWrapperController} from "@root/server/client/esweb/src/lib/stimulus/ReactWrapperController.ts";
import {Badge} from "@root/server/client/esweb/components/ui/badge.tsx";
import {Button} from "@root/server/client/esweb/components/ui/button.tsx";
import {Tabs, TabsContent, TabsList, TabsTrigger} from "@root/server/client/esweb/components/ui/tabs.tsx";
import {ChevronDown, ChevronUp, ExternalLink, Eye, EyeOff} from "lucide-react";
import {cn} from "@root/server/client/esweb/lib/utils.ts";
import {CheckValidatedData} from "@root/server/routes/app/hook_filters/CheckModel.shared.ts";
import {fetchApi} from "@root/server/client/esweb/src/lib/fetchApi";
import {type ModelObject} from "objection";
import {type HookFilterModel} from "@root/server/routes/app/hook_filters/HookFilterModel.ts";
import sanitizeHtml from 'sanitize-html';

export const name = "app--check";

export default class extends ReactWrapperController {
    constructor(context: Context) {
        super(context, (props) => <CheckRow {...props} />);
    }
}

interface CheckData {
    id: string;
    result: string;
    reason: string | null;
    payload: any | null;
    created_at: string;
    validated_data: CheckValidatedData | null;
    credit_cost: number | null;
    visited_at: string | null;
    hook_filter?: ModelObject<HookFilterModel>;
}

function CheckRow({ check }: { check: CheckData }) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [visitedAt, setVisitedAt] = useState<string | null>(check.visited_at);
    const [isToggling, setIsToggling] = useState(false);


    const toggleVisited = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isToggling) return;
        setIsToggling(true);
        try {
            const data = await fetchApi<{check: {visited_at: string | null}}>(`/app/check/${check.id}/toggle-visited`, { method: 'POST' });
            if (data?.data) {
                setVisitedAt(data.data.check.visited_at);
            }
        } finally {
            setIsToggling(false);
        }
    };

    const markAsVisitedLocally = () => {
        if (!visitedAt) {
            setVisitedAt(new Date().toISOString());
        }
    };

    let badgeVariant: "default" | "secondary" | "destructive" | "outline" = "secondary";
    let badgeClass = "";
    let badgeText = check.result;

    switch (check.result) {
        case 'MATCH':
            badgeVariant = "default";
            badgeClass = "bg-emerald-500 hover:bg-emerald-600 border-none";
            badgeText = "Match";
            break;
        case 'MISMATCH':
            badgeVariant = "secondary";
            badgeText = "Mismatch";
            break;
        case 'DUPLICATE':
            badgeVariant = "outline";
            badgeClass = "text-amber-600 border-amber-600";
            badgeText = "Duplicate";
            break;
        default:
            badgeVariant = "secondary";
            badgeText = check.result;
    }

    const links = check.validated_data?.links || [];
    const imageCount = check.validated_data?.images?.length || 0;

    const isVisited = !!visitedAt;


    const toggles = () => {
        return (
            <Button
                variant="ghost"
                size="sm"
                className={cn(
                    "h-7 px-2 flex items-center gap-1.5 font-medium transition-colors whitespace-nowrap",
                    isVisited ? "text-slate-700 bg-slate-100 hover:bg-slate-200" : "text-slate-400 hover:bg-slate-100"
                )}
                onClick={toggleVisited}
                title={isVisited ? "Mark as not visited" : "Mark as visited"}
            >
                {isVisited ? <Eye className="size-3.5" /> : <EyeOff className="size-3.5" />}
                <span className="text-[10px] uppercase tracking-wider">{isVisited ? "Visited" : "Not visited"}</span>
            </Button>
        )
    }

    const renderPreview = () => {
        const { html, text } = check.validated_data || {};

        const title  = !check.payload?.title ? null : <h3 className="text-lg font-semibold">{check.payload?.title}</h3> ;

        if (html) {
            const cleanHtml = sanitizeHtml(html, {
                allowedTags: [
                    'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'p', 'a', 'ul', 'ol',
                    'nl', 'li', 'b', 'i', 'strong', 'em', 'strike', 'code', 'hr', 'br', 'div',
                    'table', 'thead', 'caption', 'tbody', 'tr', 'th', 'td', 'pre', 'span', 'img'
                ],
                allowedAttributes: {
                    // Add 'rel' for links and 'referrerpolicy' for images
                    'a': ['href', 'name', 'target', 'rel'],
                    'img': ['src', 'alt', 'title', 'width', 'height', 'referrerpolicy'],
                    '*': ['class']
                },
                transformTags: {
                    'a': (tagName, attribs) => {
                        // If it's an external link (starts with http), add noreferrer
                        const isExternal = attribs.href && attribs.href.startsWith('http');
                        return {
                            tagName: 'a',
                            attribs: {
                                ...attribs,
                                ...(isExternal ? { rel: 'noreferrer noopener' } : {})
                            }
                        };
                    },
                    'img': (tagName, attribs) => {
                        return {
                            tagName: 'img',
                            attribs: {
                                ...attribs,
                                // Standardizes privacy for all images
                                referrerpolicy: 'no-referrer'
                            }
                        };
                    }
                }
            });

            return (
                <div 
                    className="prose prose-sm max-w-none dark:prose-invert bg-white p-4 max-sm:-mx-4 rounded border border-slate-200"
                >
                    {title}
                    <div dangerouslySetInnerHTML={{ __html: cleanHtml }}></div>
                </div>
            );
        }

        if (text) {
            return (
                <div className="bg-white p-4 max-sm:-mx-4 rounded border border-slate-200 whitespace-pre-wrap font-mono text-sm">
                    {title}
                    {text}
                </div>
            );
        }

        return null;
    };

    const hasPreview = !!(check.validated_data?.html || check.validated_data?.text);

    return (
        <div className={cn("space-y-4")}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 flex-1 min-w-0">
                    <div className="flex items-center justify-between sm:justify-start gap-4">
                        <Badge variant={badgeVariant} className={cn("flex-shrink-0 justify-center", badgeClass)}>
                            <div className="min-w-[60px] text-center">{badgeText}</div>
                        </Badge>
                        <div className="flex items-center gap-1 sm:hidden">
                            {toggles()}
                        </div>
                    </div>
                    
                    <div className="min-w-0 flex-1 space-y-1">
                        {check?.hook_filter && (
                            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                <a href={`/app/hook_filters/${check.hook_filter.id}/edit`} className="hover:text-indigo-600 hover:underline">
                                    {check.hook_filter.name}
                                </a>
                            </div>
                        )}
                        <div className="font-medium text-slate-900 break-words leading-tight" title={check.reason || ""}>
                            {check.reason || "No reason provided"}
                        </div>
                        <div className="text-xs text-slate-500 flex flex-wrap items-center gap-x-2 gap-y-1">
                            <span>{new Date(check.created_at).toLocaleString()}</span>
                            {check.credit_cost !== null && (
                                <span className="flex items-center gap-1 before:content-['•'] before:text-slate-300 before:mr-1">
                                    <span className="font-semibold text-slate-700">{check.credit_cost}</span> credits
                                </span>
                            )}
                            {imageCount > 0 && (
                                <span className="flex items-center gap-1 before:content-['•'] before:text-slate-300 before:mr-1 text-indigo-600 font-medium">
                                    {imageCount} {imageCount === 1 ? 'image' : 'images'}
                                </span>
                            )}
                        </div>
                        
                        {links.length > 0 && (
                            <div className="flex flex-wrap gap-2 pt-1">
                                {links.map((link, i) => (
                                    <a 
                                        key={i} 
                                        href={`/web/checks/${check.id}/visit/${i}`}
                                        target="_blank"
                                        data-turbo-prefetch="false"
                                        rel="noopener noreferrer"
                                        onClick={markAsVisitedLocally}
                                        onAuxClick={markAsVisitedLocally}
                                        className="inline-flex items-center gap-1 text-xs bg-indigo-50 text-indigo-700 px-2 py-1 rounded hover:bg-indigo-100 transition-colors border border-indigo-100"
                                    >
                                        <ExternalLink className="size-3" />
                                        <span className="truncate max-w-[200px]">{link.title || link.href}</span>
                                    </a>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                
                <div className="flex items-center gap-2 w-full sm:w-auto mt-2 sm:mt-0">
                     <div className="hidden sm:flex items-center gap-1 mr-2">
                        {toggles()}
                    </div>

                    <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="flex items-center justify-center gap-2 flex-1 sm:flex-initial"
                    >
                        {isExpanded ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
                        {isExpanded ? "Hide Details" : "Details"}
                    </Button>
                </div>
            </div>
            {isExpanded && (
                <div className="border-t bg-slate-50 p-4 -mb-4 sm:mb-0 max-sm:-mx-4 sm:rounded-md mt-4">
                    {hasPreview ? (
                        <Tabs defaultValue="preview" className="w-full">
                            <TabsList>
                                <TabsTrigger value="preview">Preview</TabsTrigger>
                                <TabsTrigger value="json">JSON</TabsTrigger>
                            </TabsList>
                            <TabsContent value="preview" className="mt-4">
                                {renderPreview()}
                            </TabsContent>
                            <TabsContent value="json" className="mt-4">
                                <div className="text-xs font-mono bg-slate-900 text-slate-100 p-4 max-sm:-mx-4 rounded-md overflow-x-auto">
                                    <pre className="whitespace-pre">
                                        {JSON.stringify(check, null, 2)}
                                    </pre>
                                </div>
                            </TabsContent>
                        </Tabs>
                    ) : (
                        <div className="text-xs font-mono bg-slate-900 text-slate-100 p-4 max-sm:-mx-4 rounded-md overflow-x-auto">
                            <pre className="whitespace-pre">
                                {JSON.stringify(check, null, 2)}
                            </pre>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
